import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetApp } from "../App"
import { useEditState } from "../App/useEditState"
import { classNames } from "../Components"
import { logger } from "../App/Logger"
import { apiErrorReason } from "../App/ApiError"
import { Native } from "../App/Native"
import { Session } from "../App/Session"
import { AccountInvitationResponse, LoginViewData } from "./Login/Context"
import { LoginResetDialogView } from "./Login/ResetDialogView"
import { LoginWelcomeView } from "./Login/WelcomeView"
import { LoginSignUpView } from "./Login/SignUpView"
import { LoginForgotPasswordView } from "./Login/ForgotPasswordView"
import { LoginSignInView } from "./Login/SignInView"
import { LoginInvitationView } from "./Login/InvitationView"
import { LoginFooterView } from "./Login/FooterView"
import { buildNativeLoginCallback, NATIVE_LOGIN_ROUTE, NativeLoginRequest, parseNativeLoginRequest, redirectToNativeLoginCallback } from "./Login/NativeLogin"

export { LoginViewData } from "./Login/Context"

// Seeds LoginView's internal state directly, bypassing the window.location /
// form-submission paths that normally drive it. The only consumer today is the UI
// catalog, which needs to render states with no route (a prior form's result, e.g.
// forgot-password "sent") or without a real browser location (e.g. an invitation
// route) -- see Catalog/Harness/loginApiClient.ts and Registry/Views.tsx's
// `view-login` entry.
export interface LoginViewInitialState {
  route?: string;
  message?: string;
  loginError?: string;
  // Seeds the signup box's error banner. Production only reaches it by having the
  // server reject a real signup, which the catalog can't do, so the state #1820 is
  // about -- the whole login page with the signup box in error -- was unreachable
  // there until this existed.
  signupError?: string;
  forgotPassword?: boolean | string;
  resetDialog?: number;
  // Seeds which of the login/signup boxes starts active (real inputs) when both are
  // shown together. Production always starts on "login" (the default); the catalog
  // uses this to also demonstrate the "signup selected" state.
  activeBox?: "login" | "signup";
  // Seeds the native-login request that production parses out of
  // `window.location.search`. The catalog cannot set a per-fixture query string, so
  // without this the native-login entry could only ever render its "invalid request"
  // error instead of the real Safari-sheet sign-in form.
  nativeLogin?: NativeLoginRequest;
  // Seeds whether native (Safari) sign-in is available. Production discovers this from
  // the WebKit bridge or an async capability probe, neither of which exists in the
  // catalog, so fixtures showing the Apple-shell login states seed it directly.
  nativeSignInAvailable?: boolean;
  // Keeps the catalog's Safari-pending fixture stable without opening a real sheet.
  nativeSignInPending?: boolean;
  localLoginRequired?: boolean;
  companionHandoffFailed?: boolean;
  companionHandoffPending?: boolean;
}

interface LoginViewProps {
  initial?: LoginViewInitialState;
}

export function LoginView({ initial }: LoginViewProps) {
  const app = GetApp()!;
  const t = app.getT("LoginView");
  const initialRoute = useMemo(() => {
    return initial?.route ?? window.location.pathname.split("/app/", 2)[1];
  }, []);
  const nativeLoginRoute = app.cloudSession() && initialRoute === NATIVE_LOGIN_ROUTE;
  const nativeLoginRequest = useMemo(
    () => nativeLoginRoute ? (initial?.nativeLogin ?? parseNativeLoginRequest(window.location.search)) : undefined,
    [nativeLoginRoute],
  );
  const nativeLoginCompleting = useRef(false);
  const nativeSignInStarted = useRef(false);
  const nativeSignInInProgress = useRef(false);
  const nativeSignInMounted = useRef(false);
  const companionHandoffStarted = useRef(false);
  const [companionHandoffFailed, setCompanionHandoffFailed] = useState(initial?.companionHandoffFailed ?? false);
  const companionRoute = Session.isCompanionRoute(`/app/${initialRoute}`);
  const companionHandoffPending = initial?.companionHandoffPending ?? (companionRoute && !companionHandoffFailed);
  const localLoginRequired = !app.cloudSession()
    && (initial?.localLoginRequired ?? !Session.isLoopbackHost(window.location.hostname));
  const loginBlocked = localLoginRequired || companionRoute || companionHandoffFailed || companionHandoffPending;
  useEffect(() => {
    nativeSignInMounted.current = true;
    return () => { nativeSignInMounted.current = false; };
  }, []);
  const [edited, onEdited, resetEdited] = useEditState();
  const [forgotPassword, setForgotPassword] = useState<boolean | string>(initial?.forgotPassword ?? false);
  const [invitationData, setInvitationData] = useState<AccountInvitationResponse | undefined>(undefined);
  const [invitationToken, setInvitationToken] = useState<string | undefined>(undefined);
  const [shipInvitationData, setShipInvitationData] = useState<AccountInvitationResponse | undefined>(undefined);
  const [shipInvitationToken, setShipInvitationToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(initial?.nativeSignInPending ?? false);
  const [loginError, setLoginError] = useState<string | undefined>(initial?.loginError);
  const [message, setMessage] = useState<string | undefined>(initial?.message);
  const [resetDialog, setResetDialog] = useState<number>(initial?.resetDialog ?? 0);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [signupError, setSignupError] = useState<string | undefined>(initial?.signupError);
  const [validInput, setValidInput] = useState<boolean>(false);
  const [language, setLanguage] = useState<string>(app.language || "en");
  const [nativeSignInAvailable, setNativeSignInAvailable] = useState(initial?.nativeSignInAvailable ?? Native.hasNativeSignIn);
  // Which of the login/signup boxes has real, mounted <input>s -- see LoginSignInView /
  // LoginSignUpView, which render an inert placeholder instead of a real <form> for
  // whichever box isn't active. Only meaningful when both boxes are shown together
  // (`dualBoxes` below); a lone box is always active regardless of this state.
  const [activeBox, setActiveBox] = useState<"login" | "signup">(initial?.activeBox ?? "login");
  // Native (Safari) sign-in is usable at all: the bridge or the E2E browser transport
  // is present, and this is not a cloud session. The credential form stays on screen
  // alongside it -- the Safari button leads because it is the only path where Password
  // AutoFill works from the app's localhost origin, but typing credentials must remain
  // possible (no saved password, a different account, Safari unavailable).
  const nativeSignInPossible = !app.cloudSession() && nativeSignInAvailable && !loginBlocked;
  // Opening the Safari sheet unprompted is right when the app is starting up, and wrong
  // anywhere else -- notably after a logout, which mounts this view on the in-app route
  // and would otherwise re-open the sheet on someone who just chose to leave it.
  const nativeSignInAutoStart = nativeSignInPossible
    && (!initialRoute || initialRoute === "launch" || initialRoute === "signup");
  const signupEnabled = useMemo(() => app.cloudSession() && !forgotPassword && !nativeLoginRoute, [app, forgotPassword, nativeLoginRoute]);
  const signupOnly = useMemo(() => signupEnabled && (initialRoute == "signup/app" || initialRoute == "signup/beta" || invitationData), [signupEnabled, invitationData]);
  // An existing-account ship invitation: the invitee already has a password, so force
  // the sign-in box instead of the (brand-new-user) sign-up box, without touching
  // `signupOnly`'s existing behaviour.
  const shipInvitationOnly = useMemo(() => signupEnabled && !!shipInvitationToken, [signupEnabled, shipInvitationToken]);
  const _companionRoute = useMemo(() => initialRoute?.match(/^ships\/(\d+)\/companion\/([^/]+)$/), [initialRoute]);
  const showSignupAndLogin = useMemo(() => signupEnabled && !forgotPassword && !signupOnly, [signupEnabled, forgotPassword, signupOnly]);
  // Both boxes are rendered together (see the `flex_boxes` JSX below) exactly when this
  // is true -- i.e. the only scenario where the single-active-box toggle matters. Every
  // other combination (signup-only, ship-invitation sign-in only, non-cloud sign-in
  // only, forgot-password) shows a single box, which is always active regardless of
  // `activeBox`.
  const dualBoxes = useMemo(() => signupEnabled && !shipInvitationOnly && showSignupAndLogin, [signupEnabled, shipInvitationOnly, showSignupAndLogin]);
  const activateLogin = useCallback(() => setActiveBox("login"), []);
  const activateSignup = useCallback(() => setActiveBox("signup"), []);
  // Coalesces the two invitation flows (brand-new-user signup invite vs. existing-account
  // ship invite) so the shared WelcomeView/SignInView/LoginInvitationView only need to
  // read one pair of values, regardless of which flow is active.
  const activeInvitationToken = useMemo(() => invitationToken ?? shipInvitationToken, [invitationToken, shipInvitationToken]);
  const activeInvitationData = useMemo(() => invitationData ?? shipInvitationData, [invitationData, shipInvitationData]);

  const completeNativeLogin = useCallback(async () => {
    if (!nativeLoginRequest || nativeLoginCompleting.current) {
      return;
    }
    nativeLoginCompleting.current = true;
    setLoading(true);
    setLoginError(undefined);
    try {
      const response = await LoginViewData.handoff(app.apiClient, nativeLoginRequest.state);
      redirectToNativeLoginCallback(buildNativeLoginCallback(nativeLoginRequest, response.code));
    } catch (error) {
      logger.error(error);
      nativeLoginCompleting.current = false;
      setLoginError("native_login_failed");
      setLoading(false);
    }
  }, [app.apiClient, nativeLoginRequest]);

  const startNativeSignIn = useCallback(async () => {
    if (!nativeSignInPossible || nativeSignInInProgress.current) {
      return;
    }
    nativeSignInInProgress.current = true;
    setLoading(true);
    setLoginError(undefined);
    try {
      const response = await Native.startNativeSignIn();
      if (!nativeSignInMounted.current || !response) {
        return;
      }
      if (response.session?.authorized !== true
        || typeof response.session.id !== "string"
        || !response.session.id) {
        throw new Error("Native sign-in returned an unauthorized or incomplete session");
      }
      app.setSession(response.session, response.type, response.account, response.user, response.features, response.accountUserLevel);
      app.updateRoute("");
    } catch (error) {
      logger.error("Native sign-in could not complete", error);
      if (nativeSignInMounted.current) {
        setLoginError("native_login_failed");
      }
    } finally {
      nativeSignInInProgress.current = false;
      if (nativeSignInMounted.current) {
        setLoading(false);
      }
    }
  }, [app, nativeSignInPossible]);

  const confirmEmail = useCallback(async (token: string) => {
    try {
      const response = await LoginViewData.confirmEmail(app.apiClient, token);
      if (response && response.session && response.session.authorized && response.session.id) {
        app.setSession(response.session, response.type, response.account, response.user, undefined, response.accountUserLevel);
        app.updateRoute("");
      }
    } catch (error) {
      logger.error(error);
      setLoginError("confirmation_failed");
    }
  }, []);

  const resetApp = useCallback(async () => {
    const request = LoginViewData.resetApp(app.apiClient);
    setResetDialog(2);
    const response = await request;
    if (response && response.success) {
      window.location.reload();
      setResetDialog(0);
    }
  }, []);

  useEffect(() => {
    if (!localLoginRequired || companionRoute || initial?.localLoginRequired !== undefined) return;
    const returnUrl = Session.localReturnURL(localStorage.getItem("logbook-session-return"));
    if (returnUrl && new URL(returnUrl).origin !== window.location.origin) {
      window.location.replace(`${returnUrl}?companion_disconnected=1`);
    }
  }, [localLoginRequired, companionRoute]);

  useEffect(() => {
    const params = new URLSearchParams(document.location.search);
    const companionHandoffMatch = initialRoute?.match(/^ships\/(\d+)\/companion\/handoff$/);
    const code = params.get("code");
    const state = params.get("state");
    if (companionHandoffMatch) {
      if (companionHandoffStarted.current) return;
      companionHandoffStarted.current = true;
      if (!code || !state) {
        logger.error("Companion handoff is missing its code or state");
        setCompanionHandoffFailed(true);
        return;
      }
      const shipId = parseInt(companionHandoffMatch[1]);
      void (async () => {
        try {
          const response = await LoginViewData.exchange(app.apiClient, code, state);
          if (!response.session.authorized || !response.session.id || response.type !== "local") {
            throw new Error("Companion handoff returned an unauthorized session");
          }
          localStorage.setItem("logbook-session", response.session.id);
          localStorage.setItem("logbook-ship", shipId.toString());
          const returnUrl = Session.localReturnURL(params.get("return_to"));
          localStorage.removeItem("logbook-session-return");
          if (returnUrl) {
            localStorage.setItem("logbook-session-return", returnUrl);
          }
          app.updateRoute("ships/" + shipId);
          window.location.reload();
        } catch (error) {
          logger.error(error);
          setCompanionHandoffFailed(true);
        }
      })();
      return;
    }
    const companionMatch = initialRoute?.match(/^ships\/(\d+)\/companion\/([^/]+)$/);
    if (companionMatch) {
      const shipId = parseInt(companionMatch[1]);
      const session = companionMatch[2];
      localStorage.setItem("logbook-session", session);
      localStorage.setItem("logbook-ship", shipId.toString());
      const returnUrl = Session.localReturnURL(params.get("return_to"));
      localStorage.removeItem("logbook-session-return");
      if (returnUrl) {
        localStorage.setItem("logbook-session-return", returnUrl);
      }
      app.updateRoute("ships/" + shipId);
      window.location.reload();
      return;
    }
    if (localLoginRequired) return;
    const invitationMatch = initialRoute?.match(/^signup\/invitation\/([^/]+)$/);
    if (app.hasFeature("multi-user") && app.cloudSession() && invitationMatch) {
      setInvitationToken(invitationMatch[1]);
      fetchInvitationData(invitationMatch[1]);
      return;
    }
    const shipInvitationMatch = initialRoute?.match(/^ships\/invitation\/([^/]+)$/);
    if (app.hasFeature("multi-user") && app.cloudSession() && shipInvitationMatch) {
      setShipInvitationToken(shipInvitationMatch[1]);
      fetchShipInvitationData(shipInvitationMatch[1]);
      return;
    }
    const confirmationTokenMatch = initialRoute?.match(/^signup\/confirm-email\/([^/]+)$/);
    if (confirmationTokenMatch) {
      confirmEmail(confirmationTokenMatch[1]);
      return;
    }
    const forgotPasswordTokenMatch = initialRoute?.match(/^signup\/forgot-password\/([^/]+)$/);
    if (forgotPasswordTokenMatch) {
      setForgotPassword(forgotPasswordTokenMatch[1]);
    } else if (initialRoute == "signup/forgot-password") {
      setForgotPassword(true);
    } else if (!signupEnabled && initialRoute != "signup" && !nativeLoginRoute) {
      app.updateRoute("signup");
    }
  }, []);

  useEffect(() => {
    if (loginBlocked || initial?.nativeSignInAvailable !== undefined || app.cloudSession() || Native.hasNativeSignIn()) {
      return;
    }

    let mounted = true;
    void Native.nativeSignInAvailable().then((available) => {
      if (mounted) {
        setNativeSignInAvailable(available);
      }
    });
    return () => {
      mounted = false;
    };
  }, [app]);

  useEffect(() => {
    if (nativeLoginRequest && app.session?.authorized) {
      void completeNativeLogin();
    }
  }, [app.session?.authorized, completeNativeLogin, nativeLoginRequest]);

  useEffect(() => {
    // `Native.hasNativeSignIn()` (not `nativeSignInPossible`): only the real WebKit
    // bridge auto-opens. The E2E browser transport uses `window.open`, which needs a
    // user gesture to survive popup blocking.
    if (nativeSignInAutoStart && Native.hasNativeSignIn() && !nativeSignInStarted.current) {
      nativeSignInStarted.current = true;
      void startNativeSignIn();
    }
  }, [nativeSignInAutoStart, startNativeSignIn]);

  const fetchInvitationData = useCallback(async (token: string) => {
    try {
      const response = await LoginViewData.loadInvitation(app.apiClient, token);
      if (response && response.email) {
        setInvitationData(response);
        setMessage(undefined);
      } else {
        setMessage("invitation_invalid");
        setInvitationToken(undefined);
      }
    } catch (error) {
      logger.error(error);
      setMessage("invitation_invalid");
      setInvitationToken(undefined);
    }
  }, []);

  const fetchShipInvitationData = useCallback(async (token: string) => {
    try {
      const response = await LoginViewData.loadShipInvitation(app.apiClient, token);
      if (response && response.email) {
        setShipInvitationData(response);
        setMessage(undefined);
      } else {
        setMessage("invitation_invalid");
        setShipInvitationToken(undefined);
      }
    } catch (error) {
      logger.error(error);
      setMessage("invitation_invalid");
      setShipInvitationToken(undefined);
    }
  }, []);

  const submit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.target as HTMLFormElement);
    const email = data.get("email") as string;
    const password = data.get("password") as string;
    setLoading(true);
    setLoginError(undefined);
    try {
      const response = await LoginViewData.login(app.apiClient, { "email": email, "password": password, "client": app.cloudSession() ? "cloud" : undefined });
      if (response && response.session && response.session.authorized && response.session.id) {
        app.setSession(response.session, response.type, response.account, response.user, response.features, response.accountUserLevel);
        if (nativeLoginRequest) {
          await completeNativeLogin();
          return;
        }
        // Skip the normal post-login redirect on a ship-invitation route: the invitee
        // still needs to see (and act on) the LoginInvitationView accept UI before
        // navigating away.
        if (!shipInvitationToken) {
          app.updateRoute("");
        }
      } else if (response && response.session && !response.session.authorized) {
        setLoginError("failed");
      }
      setLoading(false);
    } catch (error) {
      logger.error(error);
      setLoginError("failed_request");
      setLoading(false);
    }
  }, [completeNativeLogin, nativeLoginRequest, shipInvitationToken]);

  const submitReset = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.target as HTMLFormElement);
    const email = data.get("email") as string;
    setLoading(true);
    setLoginError(undefined);
    try {
      const response = await LoginViewData.forgotPassword(app.apiClient, { "email": email });
      if (response && response.success == true) {
        setForgotPassword("success");
      } else {
        setLoginError("reset_failed");
      }
      setLoading(false);
    } catch (error) {
      logger.error(error);
      setLoginError("reset_failed_request");
      setLoading(false);
    }
  }, []);

  const submitResetComplete = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.target as HTMLFormElement);
    const password = data.get("password") as string;
    const passwordConfirmation = data.get("passwordConfirmation") as string;
    setLoading(true);
    setLoginError(undefined);
    try {
      const response = await LoginViewData.resetPassword(app.apiClient, forgotPassword as string, { "password": password, "passwordConfirmation": passwordConfirmation });
      if (response && response.success == true) {
        setForgotPassword("complete");
      } else {
        setLoginError("reset_failed");
      }
      setLoading(false);
    } catch (error) {
      logger.error(error);
      setLoginError("reset_failed_request");
      setLoading(false);
    }
  }, [forgotPassword]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const saveEdits = useCallback(async (_data: FormData, values: any) => {
    setLoading(true);
    setSignupError(undefined);
    if (invitationToken) {
      const response = await LoginViewData.acceptInvitation(app.apiClient, invitationToken, values);
      if (response && response.session && response.session.authorized && response.session.id) {
        app.setSession(response.session, response.type, response.account, response.user, undefined, response.accountUserLevel);
        app.updateRoute("");
      } else if (response && response.session && !response.session.authorized) {
        setLoginError("failed");
      }
    } else {
      // Every rejection ends up here, named or not (#1820). The failure branch used to
      // run only for a wire shape the server stopped sending, so a rejected signup left
      // no message AND no `setLoading(false)` -- the submit button simply died. Reading
      // the reason is now the only thing that depends on the error's shape; clearing the
      // form state is unconditional.
      let response;
      try {
        response = await LoginViewData.createAccount(app.apiClient, values);
      } catch (error) {
        logger.error(error);
        setSignupError(apiErrorReason(error) || "signup_failed");
        // Keeps submit disabled until something is actually changed, so a retry cannot
        // repost the exact request that was just refused.
        setValidInput(false);
        setLoading(false);
        resetEdited();
        return;
      }
      if (response && response.success) {
        setMessage("signup_success");
      }
      setLoading(false);
    }
  }, [invitationToken]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const validateEdits = useCallback((_data: FormData, values: any): boolean => {
    const valid = LoginViewData.validateSignup(values, signupError);
    setValidInput(valid);
    resetEdited();
    return valid;
  }, [resetEdited, signupError]);

  const languageOptions = useMemo(() => [
    { title: "English", value: "en" },
    { title: "Deutsch", value: "de" },
    { title: "Français", value: "fr" },
  ], []);

  const handleLanguageChange = useCallback((newLang: string) => {
    setLanguage(newLang);
    app.setLanguage(newLang);
  }, [app]);

  const languageButtonText = useMemo(() => {
    const option = languageOptions.find((opt) => opt.value === language);
    return option?.title || language.toUpperCase();
  }, [language, languageOptions]);

  const acceptInvitation = useCallback(async () => {
    setLoading(true);
    setLoginError(undefined);
    try {
      if (shipInvitationToken) {
        await LoginViewData.acceptShipInvitation(app.apiClient, shipInvitationToken);
        app.updateRoute("");
      } else {
        // Pre-existing stub: a brand-new-user signup-invitation link visited while
        // already logged in as a different user has no attach action defined yet
        // (out of scope for this issue -- RootView only forces LoginView to stay
        // mounted for the *ship*-invitation route, so this branch stays as-is).
        logger.debug("Accepting invitation");
        app.updateRoute("");
      }
    } catch (error) {
      logger.error(error);
      setLoginError("invitation_accept_failed");
    }
    setLoading(false);
  }, [app, invitationData, shipInvitationToken]);

  const useOtherAccount = useCallback(async () => {
    // Log out current user
    try {
      await LoginViewData.deleteSession(app.apiClient);
      window.location.reload();
    } catch (error) {
      logger.error(error);
    }
  }, [app]);

  return (
    <div className={classNames(["LoginView", signupEnabled && showSignupAndLogin ? "signup-enabled" : "login-only", signupOnly && "signup-only"])}><div className="logo">
      <a href="https://logbook.yachts" target="_blank" rel="noopener noreferrer">
        <img src={app.theme === "light" ? "/logbook-logo-all-black.svg" : "/logbook-logo-all.svg"} />
      </a>
    </div>

    {resetDialog > 0 && (
      <LoginResetDialogView resetDialog={resetDialog} setResetDialog={setResetDialog} resetApp={resetApp} />
    )}

    {/* The native-login route renders inside the Safari sheet
        (ASWebAuthenticationSession), which is short: the welcome header and footer
        pushed the credential form below the fold on iPad. The logo stays -- small, and
        brand assurance matters on a password screen. */}
    {!nativeLoginRoute && !loginBlocked && (
      <LoginWelcomeView message={message} invitationToken={activeInvitationToken} invitationData={activeInvitationData} />
    )}

    {loginBlocked && (
      <div className="LoginView-error LoginView-local-login-required" role="status">
        {t(companionHandoffFailed ? "companion_handoff_failed" : companionHandoffPending ? "companion_handoff_pending" : "local_login_required")}
      </div>
    )}

    {nativeLoginRoute && (!nativeLoginRequest || loginError == "native_login_failed") && (
      <div className="LoginView-error LoginView-native-login-error">
        {t(`error_${nativeLoginRequest ? "native_login_failed" : "native_login_invalid"}`)}
      </div>
    )}

    {/* Boxes for login, signup, forgot password  */}
    {!loginBlocked && !message && !(activeInvitationToken && app.user) && (!nativeLoginRoute || (!!nativeLoginRequest && !app.session?.authorized)) && (
      <div className="flex_boxes">
        {signupEnabled && !shipInvitationOnly && (
          <LoginSignUpView active={!dualBoxes || activeBox === "signup"} onActivate={activateSignup} edited={edited} onEdited={onEdited} saveEdits={saveEdits} validateEdits={validateEdits} signupError={signupError} showPassword={showPassword} setShowPassword={setShowPassword} validInput={validInput} loading={loading} invitationData={invitationData} invitationToken={invitationToken} />
        )}
        {forgotPassword && (
          <LoginForgotPasswordView forgotPassword={forgotPassword} loginError={loginError} loading={loading} showPassword={showPassword} setShowPassword={setShowPassword} submitReset={submitReset} submitResetComplete={submitResetComplete} setForgotPassword={setForgotPassword} onEdited={onEdited} />
        )}
        {(!signupEnabled || showSignupAndLogin) && !forgotPassword && (
          <LoginSignInView active={!dualBoxes || activeBox === "login"} onActivate={activateLogin} loginError={loginError} loading={loading} showPassword={showPassword} setShowPassword={setShowPassword} submit={submit} invitationData={activeInvitationData} setForgotPassword={setForgotPassword} onNativeSignIn={nativeSignInPossible ? () => void startNativeSignIn() : undefined} />
        )}
      </div>
    )}

    {/* Invitation action buttons when user is already logged in */}
    {!message && activeInvitationData && app.user && (
      <LoginInvitationView loading={loading} acceptInvitation={acceptInvitation} useOtherAccount={useOtherAccount} loginError={loginError} />
    )}

    {!nativeLoginRoute && !loginBlocked && (
      <LoginFooterView language={language} languageButtonText={languageButtonText} languageOptions={languageOptions} handleLanguageChange={handleLanguageChange} setResetDialog={setResetDialog} />
    )}
    </div>
  );
}
